import type { TFunction } from 'i18next';

import appEn from '../locales/app/en.json';
import appAr from '../locales/app/ar.json';
import appUr from '../locales/app/ur.json';
import appHi from '../locales/app/hi.json';
import appBn from '../locales/app/bn.json';
import appSi from '../locales/app/si.json';
import appNe from '../locales/app/ne.json';
import type { HelpLanguage } from '../i18n/help';

type AppLocale = typeof appEn;

const APP_LOCALES: Record<HelpLanguage, AppLocale> = {
  en: appEn,
  ar: appAr,
  ur: appUr,
  hi: appHi,
  bn: appBn,
  si: appSi,
  ne: appNe,
};

function getNested(obj: unknown, path: string): string | undefined {
  const val = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as object)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
  return typeof val === 'string' ? val : undefined;
}

/** English UI labels in help article bodies → app locale key paths. Longest first. */
const APP_UI_TERM_KEYS: [string, string][] = [
  ['Add line', 'supply.addLine'],
  ['Month tab', 'knowledgeBase.uiTerms.monthTab'],
  ['Week tab', 'knowledgeBase.uiTerms.weekTab'],
  ['Unread tab', 'notifications.tabUnread'],
  ['Read tab', 'notifications.tabRead'],
  [' ton)', ' units.tons)'],
  [' ton.', ' units.tons.'],
  [' ton,', ' units.tons,'],
  [' ton ', ' units.tons '],
  ['Accept extra deliveries toggle', 'knowledgeBase.uiTerms.acceptExtraDeliveriesToggle'],
  ['Accept extra deliveries switch', 'knowledgeBase.uiTerms.acceptExtraDeliveriesSwitch'],
  ['Accept extra deliveries', 'supply.acceptsExtra'],
  ['Weekly / Monthly', 'knowledgeBase.uiTerms.weeklyMonthlyToggle'],
  ['Mark all as read', 'notifications.markAllAsRead'],
  ['Knowledge Base', 'knowledgeBase.title'],
  ['Excess not allowed', 'dashboard.excessNotAllowed'],
  ['Fulfillment Dashboard', 'knowledgeBase.uiTerms.fulfillmentDashboard'],
  ['Dashboard fulfillment', 'dashboard.fulfillment'],
  ['Supply targets', 'supply.title'],
  ['Supply Targets', 'supply.title'],
  ['Supply Overview', 'knowledgeBase.uiTerms.supplyOverview'],
  ['Dashboard Overview', 'knowledgeBase.uiTerms.dashboardOverview'],
  ['Edit Supply', 'knowledgeBase.uiTerms.editSupply'],
  ['Over target', 'dashboard.overTarget'],
  ['Mark as read', 'notifications.markAsRead'],
  ['Dark mode switch', 'knowledgeBase.uiTerms.darkModeSwitch'],
  ['Go to Supply', 'dashboard.goToSupply'],
  ['Open Dashboard', 'knowledgeBase.uiTerms.openDashboard'],
  ['Open Supply', 'knowledgeBase.uiTerms.openSupply'],
  ['target kg', 'dashboard.targetKg'],
  ['received kg', 'dashboard.receivedKg'],
  ['Supply page', 'knowledgeBase.uiTerms.supplyPage'],
  ['The Supply page', 'knowledgeBase.uiTerms.theSupplyPage'],
  ['On the Supply page', 'knowledgeBase.uiTerms.onTheSupplyPage'],
  ['Notifications', 'nav.notifications'],
  ['Dashboard', 'nav.dashboard'],
  ['Supply', 'nav.supply'],
  ['Refresh', 'common.refresh'],
  ['Languages', 'account.languages'],
  ['Help', 'nav.help'],
  ['Market', 'knowledgeBase.uiTerms.platformName'],
  ['Unread', 'notifications.tabUnread'],
  ['Weekly', 'repeat.weekly'],
  ['Monthly', 'repeat.monthly'],
  ['Save', 'knowledgeBase.uiTerms.save'],
  ['Month', 'knowledgeBase.uiTerms.monthTab'],
  ['Week', 'knowledgeBase.uiTerms.weekTab'],
  ['English', 'languages.english'],
  ['Arabic', 'languages.arabic'],
  ['Urdu', 'languages.urdu'],
  ['Hindi', 'languages.hindi'],
  ['Bengali', 'languages.bengali'],
  ['Sinhala', 'languages.sinhala'],
  ['Nepali', 'languages.nepali'],
  ['Dark mode', 'account.darkMode'],
  ['Read', 'notifications.tabRead'],
  ['fulfillment', 'dashboard.fulfillment'],
  [' ton', ' units.tons'],
  ['kg', 'units.kg'],
];

/** Contextual English phrases in article bodies → knowledgeBase.uiTerms keys. */
const HELP_UI_TERM_KEYS: [string, string][] = [
  ['organization dropdown', 'knowledgeBase.uiTerms.organizationDropdown'],
  ['sidebar dropdown', 'knowledgeBase.uiTerms.sidebarDropdown'],
  ['footer section', 'knowledgeBase.uiTerms.footerSection'],
  ['account menu', 'knowledgeBase.uiTerms.accountMenu'],
  ['material table', 'knowledgeBase.uiTerms.materialTable'],
  ['material lines', 'knowledgeBase.uiTerms.materialLines'],
  ['summary cards', 'knowledgeBase.uiTerms.summaryCards'],
  ['progress bar', 'knowledgeBase.uiTerms.progressBar'],
  ['status chip', 'knowledgeBase.uiTerms.statusChip'],
  ['delete icon', 'knowledgeBase.uiTerms.deleteIcon'],
  ['menu button', 'knowledgeBase.uiTerms.menuButton'],
  ['page header', 'knowledgeBase.uiTerms.pageHeader'],
  ['collapse control', 'knowledgeBase.uiTerms.collapseControl'],
  ['icon-only mode', 'knowledgeBase.uiTerms.iconOnlyMode'],
  ['unread badge', 'knowledgeBase.uiTerms.unreadBadge'],
  ['top bar', 'knowledgeBase.uiTerms.topBar'],
  ['dropdown', 'knowledgeBase.uiTerms.dropdown'],
  ['tooltip', 'knowledgeBase.uiTerms.tooltip'],
  ['sidebar', 'knowledgeBase.uiTerms.sidebar'],
  ['toggle', 'knowledgeBase.uiTerms.toggle'],
  ['switch', 'knowledgeBase.uiTerms.switch'],
  ['scroll', 'knowledgeBase.uiTerms.scroll'],
  ['hover', 'knowledgeBase.uiTerms.hover'],
  ['expand', 'knowledgeBase.uiTerms.expand'],
  ['collapse', 'knowledgeBase.uiTerms.collapse'],
  ['chip', 'knowledgeBase.uiTerms.chip'],
  ['badge', 'knowledgeBase.uiTerms.badge'],
];

const SORTED_APP_TERMS = [...APP_UI_TERM_KEYS].sort((a, b) => b[0].length - a[0].length);
const SORTED_HELP_TERMS = [...HELP_UI_TERM_KEYS].sort((a, b) => b[0].length - a[0].length);

export function localizeHelpUiTerms(
  text: string,
  language: HelpLanguage,
  appT: TFunction
): string {
  if (!text || language === 'en') return text;

  const appLoc = APP_LOCALES[language];
  let result = text;

  for (const [english, key] of SORTED_APP_TERMS) {
    const translated = key.startsWith('knowledgeBase.')
      ? appT(key)
      : getNested(appLoc, key);
    if (translated && translated !== english && translated !== key) {
      result = result.split(english).join(translated);
    }
  }

  for (const [english, key] of SORTED_HELP_TERMS) {
    const translated = appT(key);
    if (translated && translated !== english && translated !== key) {
      result = result.split(english).join(translated);
    }
  }

  return result;
}
